import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  Observable,
  Subscription,
  tap,
} from 'rxjs';
import { LetDirective } from '@ngrx/component';
import { DOCUMENT, NgIf } from '@angular/common';
import { WINDOW } from '@ng-web-apis/common';
import { MediaStore } from '@nx-massaud/media/ui';
import { ImageFile, ImageMedia } from '@madeinlune/drupal-types';
import { MatIconModule } from '@angular/material/icon';

export type ObjectFit = 'cover' | 'contain';

export const profiles = [64, 256, 512, 640, 1024, 2048] as const;
export type Profiles = (typeof profiles)[number];

@Component({
  selector: 'i2l-media-image',
  standalone: true,
  exportAs: 'mediaImageComponent',
  template: `
    <!--<ng-container *ngrxLet="loading$; let loading">
      <ng-container *ngIf="loading">
        <mat-icon [svgIcon]="'downloading'" class="cmp-loading"></mat-icon>
      </ng-container>
    </ng-container>-->
    <img #imageElement />
    <ng-content></ng-content>
  `,
  styleUrls: ['./media-image.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LetDirective, NgIf, MatIconModule],
  providers: [MediaStore],
})
export class MediaImageComponent
  extends ComponentStore<{
    imageMedia?: ImageMedia;
  }>
  implements OnDestroy, AfterViewInit, OnInit
{
  private readonly document = inject(DOCUMENT);

  private documentResizeSub: Subscription | undefined;

  imageMedia$ = this.select((s) => s.imageMedia);

  loading$ = this.mediaStore.loading$;

  setMedia = this.effect((media$: Observable<ImageMedia | undefined>) => {
    return media$.pipe(
      filter((media) => !!media),
      distinctUntilChanged((oldValue, newValue) => {
        return oldValue?.id === newValue?.id;
      }),
      tap((media) => {
        if (media) {
          if (media.alt) {
            this.mediaStore.alt = media.alt;
          }
          const averageColors = media.average_colors;
          if (averageColors) {
            const { average_color, isDark } = averageColors;
            if (average_color) {
              this.el.nativeElement.style.setProperty(
                '--background-color',
                average_color
              );
            }
            this.darkImageEmitter.emit(isDark);
          }
          const focusPoint = media.focusPoint;
          if (
            focusPoint &&
            focusPoint.x &&
            focusPoint.y &&
            this.#imageElement?.nativeElement
          ) {
            this.#imageElement.nativeElement.style.objectPosition = `${
              focusPoint.x * 100
            }% ${focusPoint.y * 100}%`;
          }
          if (this.#imageElement?.nativeElement) {
            this.#imageElement?.nativeElement.setAttribute(
              'width',
              `${media.width}`
            );
            this.#imageElement?.nativeElement.setAttribute(
              'height',
              `${media.height}`
            );
            this.#imageElement?.nativeElement.style.setProperty(
              '--media-image--aspect-ratio',
              `${media.width}/${media.height}`
            );
          }
          this.createResizeObserver(media);
        }
      })
    );
  });

  /*setImageLoaded = this.effect((imageLoaded$: Observable<boolean>) => {
    return imageLoaded$.pipe(
      delay(100),
      tap((imageLoaded) => {
        if (imageLoaded && this.#imageElement) {
        }
      })
    );
  });*/

  @Input()
  set profile(profile: Profiles) {}

  @Input()
  set forceLoad(forceLoad: boolean) {
    this.mediaStore.forceLoading = forceLoad;
  }

  @Input()
  set alt(alt: string | undefined) {
    this.mediaStore.alt = alt;
  }

  @Input()
  set image(imageMedia: ImageMedia | undefined) {
    this.patchState({ imageMedia });
  }

  #objectFit: ObjectFit = 'cover';
  @Input()
  set objectFit(value: ObjectFit) {
    this.#validateObjectFit(value, this.#objectFit);
    this.#objectFit = value;
  }

  @Input()
  forceHeight: boolean = false;

  #imageElement: ElementRef<HTMLImageElement> | undefined;
  @ViewChild('imageElement') set imageElement(
    imageElement: ElementRef<HTMLImageElement>
  ) {
    this.#imageElement = imageElement;
  }

  #resizeObserver!: ResizeObserver;

  @Output()
  darkImageEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

  // darkImage = false;

  constructor(
    private renderer: Renderer2,
    private el: ElementRef<HTMLElement>,
    @Inject(PLATFORM_ID) platformId: string,
    @Inject(WINDOW) private window: Window,
    private mediaStore: MediaStore
  ) {
    super({});
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.destroyResizeObserver();
    if (this.documentResizeSub) {
      this.documentResizeSub.unsubscribe();
    }
  }

  createResizeObserver(media: ImageMedia) {
    this.destroyResizeObserver();
    this.#resizeObserver = new ResizeObserver((entries) => {
      this.setSrc(media);
    });
    this.#resizeObserver.observe(this.el.nativeElement);
    this.setSrc(media);
  }

  destroyResizeObserver() {
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }
  }

  #validateObjectFit(
    newValue: ObjectFit,
    oldValue: ObjectFit | undefined = undefined
  ) {
    const { nativeElement } = this.el;
    if (oldValue) {
      this.renderer.removeClass(nativeElement, `object-fit-${oldValue}`);
    }
    this.renderer.addClass(nativeElement, `object-fit-${newValue}`);
  }

  setSrc(imageMedia: ImageMedia) {
    const imageProfiles = imageMedia?.images;
    if (imageProfiles) {
      const { nativeElement } = this.el;
      const isVertical = nativeElement.offsetHeight > nativeElement.offsetWidth;
      let imageFile: ImageFile | undefined;
      const resizedProfiles = imageProfiles.filter(
        (p) => p.profileId.indexOf('crop') == -1
      );
      const maxProfileWidth = Math.max(...resizedProfiles.map((o) => o.width));
      const maxProfileHeight = Math.max(
        ...resizedProfiles.map((o) => o.height)
      );
      const devicePixelRatio = this.window.devicePixelRatio >= 2 ? 2 : 1;
      if (isVertical) {
        imageFile = resizedProfiles.find(
          (i) => i.height >= nativeElement.offsetHeight * devicePixelRatio
        );
        if (!imageFile) {
          imageFile = resizedProfiles.find(
            (i) => i.height === maxProfileHeight
          );
        }
      } else {
        imageFile = resizedProfiles.find((i) => {
          const elementWidth = nativeElement.offsetWidth;
          const profileWidth = i.width;
          return profileWidth >= elementWidth * devicePixelRatio;
        });
        if (!imageFile) {
          imageFile = resizedProfiles.find((i) => i.width === maxProfileWidth);
        }
      }
      if (imageFile) {
        // this.el.nativeElement.style.aspectRatio = `${imageFile.width}/${imageFile.height}`;
        this.mediaStore.url = imageFile.webpUrl;
        this.mediaStore.size = {
          width: imageFile.width,
          height: imageFile.height,
        };
      }
    }
  }

  ngOnInit() {
    //this.setImageLoaded(this.mediaStore.loaded$);
  }

  ngAfterViewInit(): void {
    this.setMedia(this.imageMedia$);
    this.#validateObjectFit(this.#objectFit);
    if (this.#imageElement) {
      this.mediaStore.target = this.#imageElement.nativeElement;
    }
    const rootStyles = getComputedStyle(this.el.nativeElement);
    const preventResize = rootStyles.getPropertyValue('--prevent-resize');
    if (this.forceHeight && this.document.defaultView && !preventResize) {
      this.updateHeight();
      this.documentResizeSub = fromEvent(this.document.defaultView, 'resize')
        .pipe(debounceTime(250))
        .subscribe(() => {
          this.updateHeight();
        });
    }
  }

  private updateHeight() {
    this.el.nativeElement.style.setProperty(
      '--height',
      `${this.el.nativeElement.offsetHeight}px`
    );
    this.el.nativeElement.style.setProperty(
      '--img-height',
      `${this.el.nativeElement.offsetHeight}px`
    );
  }
}
